import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import { Calendar, CaretLeft, CaretRight } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

export type DateTimePickerMode = 'datetime' | 'date' | 'time'

export interface DateTimePickerProps {
  /** Value string. Format depends on `mode`:
   *  - `'datetime'`: `YYYY-MM-DDTHH:mm`
   *  - `'date'`: `YYYY-MM-DD`
   *  - `'time'`: `HH:mm` */
  value?: string
  onChange: (value: string) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Optional ARIA label when no `label` prop is provided. */
  ariaLabel?: string
  /** Which fields to render. Default `'datetime'`. */
  mode?: DateTimePickerMode
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function parseValue(v: string | undefined, mode: DateTimePickerMode): Date | null {
  if (!v) return null
  if (mode === 'time') {
    const m = v.match(/^(\d{1,2}):(\d{2})$/)
    if (m) {
      const d = new Date()
      d.setHours(Number(m[1]), Number(m[2]), 0, 0)
      return d
    }
  }
  if (mode === 'date') {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    }
  }
  // Datetime — also handles partial `YYYY-MM-DD` fallback.
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/)
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      m[4] ? Number(m[4]) : 0,
      m[5] ? Number(m[5]) : 0,
    )
  }
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}

function serialize(d: Date, mode: DateTimePickerMode): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  if (mode === 'date') {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }
  if (mode === 'time') {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  }
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// "MMM d, yyyy" (e.g. "Jul 20, 2026") — the date format used consistently
// across the app (campaign detail timeline, billing, etc.), rather than a
// locale-ambiguous DD/MM/YYYY.
function formatDisplay(d: Date, mode: DateTimePickerMode): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const datePart = format(d, 'MMM d, yyyy')
  const h12 = d.getHours() % 12 || 12
  const ampm = d.getHours() < 12 ? 'AM' : 'PM'
  const timePart = `${pad(h12)}:${pad(d.getMinutes())} ${ampm}`
  if (mode === 'date') return datePart
  if (mode === 'time') return timePart
  return `${datePart}, ${timePart}`
}

function getMonthGrid(year: number, monthIdx: number) {
  const first = new Date(year, monthIdx, 1)
  const startDay = first.getDay()
  const start = new Date(year, monthIdx, 1 - startDay)
  const grid: { date: Date; inMonth: boolean }[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    grid.push({ date: d, inMonth: d.getMonth() === monthIdx })
  }
  return grid
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  ariaLabel,
  mode = 'datetime',
}) => {
  const showDate = mode !== 'time'
  const showTime = mode !== 'date'
  const resolvedPlaceholder =
    placeholder ?? (mode === 'date' ? 'Pick date' : mode === 'time' ? 'Pick time' : 'Pick date & time')

  const selectedDate = parseValue(value, mode)
  const [open, setOpen] = useState(false)
  const [monthCursor, setMonthCursor] = useState<Date>(() => selectedDate ?? new Date())
  const [view, setView] = useState<'days' | 'months' | 'years'>('days')
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const POPOVER_WIDTH = 288
  const POPOVER_HEIGHT = mode === 'time' ? 140 : mode === 'date' ? 300 : 360

  // Keep month cursor in sync with selected date when value changes
  useEffect(() => {
    if (selectedDate) setMonthCursor(selectedDate)
  }, [value])

  // Reset to days view whenever popover closes so re-open starts fresh
  useEffect(() => {
    if (!open) setView('days')
  }, [open])

  const positionMenu = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const top = spaceBelow < POPOVER_HEIGHT && rect.top > POPOVER_HEIGHT
      ? rect.top - POPOVER_HEIGHT - 6
      : rect.bottom + 6
    // Right-align the popover with the trigger so it never overflows the viewport.
    let left = rect.right - POPOVER_WIDTH
    if (left < 8) left = 8
    if (left + POPOVER_WIDTH > window.innerWidth - 8) {
      left = window.innerWidth - POPOVER_WIDTH - 8
    }
    setCoords({ top, left })
  }

  const openPicker = () => {
    if (disabled) return
    positionMenu()
    setOpen(true)
  }
  const closePicker = () => setOpen(false)

  // Outside click + scroll/resize dismiss
  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      // Time wheel popover lives in its own portal — treat clicks inside
      // it as inside-the-picker so the parent doesn't close.
      const wheel = document.getElementById('time-wheel-menu')
      if (wheel?.contains(target)) return
      closePicker()
    }
    // Filter scroll: only close on outer scrolls (page-level), not on
    // internal scrolls inside the popover or the time-wheel sub-popover.
    // Click-to-focus on the time input auto-scrolls it into view → fired
    // this listener and closed the whole widget. That was the bug.
    const onScroll = (e: Event) => {
      const target = e.target as Node | Document | null
      if (!target) return
      if (target instanceof Node) {
        if (menuRef.current?.contains(target)) return
        const wheelMenu = document.getElementById('time-wheel-menu')
        if (wheelMenu?.contains(target)) return
      }
      closePicker()
    }
    const onResize = () => closePicker()
    document.addEventListener('mousedown', onMouseDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  // Esc closes
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const commitDate = (next: Date) => onChange(serialize(next, mode))

  const handleDayClick = (date: Date) => {
    const base = selectedDate ?? new Date()
    const next = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      base.getHours(),
      base.getMinutes(),
    )
    commitDate(next)
    if (mode === 'date') closePicker()
  }

  const handleHourChange = (input: number) => {
    const base = selectedDate ?? new Date()
    const wasPM = base.getHours() >= 12
    const h = Math.max(1, Math.min(12, input))
    const h24 = (h % 12) + (wasPM ? 12 : 0)
    const next = new Date(base)
    next.setHours(h24)
    commitDate(next)
  }

  const handleMinuteChange = (input: number) => {
    const base = selectedDate ?? new Date()
    const m = Math.max(0, Math.min(59, input))
    const next = new Date(base)
    next.setMinutes(m)
    commitDate(next)
  }

  const handleAmPmToggle = () => {
    const base = selectedDate ?? new Date()
    const next = new Date(base)
    next.setHours((next.getHours() + 12) % 24)
    commitDate(next)
  }

  const handleNow = () => {
    const now = new Date()
    commitDate(now)
    setMonthCursor(now)
  }

  const todayKey = new Date().toDateString()
  const selKey = selectedDate?.toDateString()
  const grid = getMonthGrid(monthCursor.getFullYear(), monthCursor.getMonth())

  const baseTime = selectedDate ?? new Date()
  const h12 = baseTime.getHours() % 12 || 12
  const m = baseTime.getMinutes()
  const isPM = baseTime.getHours() >= 12

  return (
    <div className={cn('relative w-full', className)}>
      {label && (
        <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        disabled={disabled}
        aria-label={ariaLabel || label || resolvedPlaceholder}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'group flex h-9 w-full items-center justify-between rounded-md border px-3 transition-colors duration-150',
          'bg-background border-border/60 text-foreground',
          'hover:border-border/80 hover:bg-background',
          'focus:outline-none focus-visible:border-[#7170ff]/55',
          'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8]',
          'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
          'dark:focus-visible:border-[#7170ff]/75',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-[#7170ff]/55 dark:border-[#7170ff]/75',
        )}
        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
      >
        <span
          className={cn(
            'tabular-nums truncate',
            selectedDate
              ? 'text-foreground dark:text-[#f7f8f8]'
              : 'text-muted-foreground/60 dark:text-[#62666d]',
          )}
        >
          {selectedDate ? formatDisplay(selectedDate, mode) : resolvedPlaceholder}
        </span>
        <Calendar
          className={cn(
            'h-4 w-4 flex-shrink-0 ml-2 transition-colors duration-150',
            open
              ? 'text-[#5e6ad2] dark:text-[#a5b4fc]'
              : 'text-muted-foreground dark:text-[#8a8f98]',
            'group-hover:text-[#5e6ad2] dark:group-hover:text-[#a5b4fc]',
          )}
          weight="regular"
        />
      </button>

      {/* Portal popover */}
      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && coords && (
              <motion.div
                ref={menuRef}
                role="dialog"
                aria-modal="false"
                aria-label={mode === 'date' ? 'Date picker' : mode === 'time' ? 'Time picker' : 'Date and time picker'}
                initial={{ opacity: 0, scale: 0.97, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: -4 }}
                transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  position: 'fixed',
                  top: coords.top,
                  left: coords.left,
                  width: POPOVER_WIDTH,
                  zIndex: 200,
                }}
                className={cn(
                  'rounded-lg border shadow-lg overflow-hidden',
                  'bg-card border-border/60',
                  'dark:bg-[#191a1b] dark:border-white/[0.08]',
                  'dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
                )}
              >
                {showDate && (() => {
                  // Shared header chrome — title is clickable to step up a view
                  // (days → months → years). Prev/next nav cycles by month / year
                  // / decade depending on the current view.
                  const headerLabel =
                    view === 'days'
                      ? `${MONTHS[monthCursor.getMonth()]} ${monthCursor.getFullYear()}`
                      : view === 'months'
                        ? `${monthCursor.getFullYear()}`
                        : (() => {
                            const startYear = Math.floor(monthCursor.getFullYear() / 12) * 12
                            return `${startYear} – ${startYear + 11}`
                          })()

                  const navPrev = () => {
                    if (view === 'days') {
                      setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))
                    } else if (view === 'months') {
                      setMonthCursor(new Date(monthCursor.getFullYear() - 1, monthCursor.getMonth(), 1))
                    } else {
                      setMonthCursor(new Date(monthCursor.getFullYear() - 12, monthCursor.getMonth(), 1))
                    }
                  }
                  const navNext = () => {
                    if (view === 'days') {
                      setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))
                    } else if (view === 'months') {
                      setMonthCursor(new Date(monthCursor.getFullYear() + 1, monthCursor.getMonth(), 1))
                    } else {
                      setMonthCursor(new Date(monthCursor.getFullYear() + 12, monthCursor.getMonth(), 1))
                    }
                  }
                  const titleClick = () => {
                    setView(view === 'days' ? 'months' : view === 'months' ? 'years' : 'days')
                  }

                  return (
                    <>
                      {/* Header */}
                      <div className="flex items-center justify-between px-2.5 pt-2.5 pb-1.5">
                        <button
                          type="button"
                          onClick={navPrev}
                          aria-label={`Previous ${view === 'days' ? 'month' : view === 'months' ? 'year' : 'decade'}`}
                          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
                        >
                          <CaretLeft className="h-3.5 w-3.5" weight="bold" />
                        </button>
                        <button
                          type="button"
                          onClick={titleClick}
                          aria-label="Switch view"
                          className="px-2 py-0.5 rounded-md text-foreground hover:bg-muted/40 dark:text-[#f7f8f8] dark:hover:bg-white/[0.05] tabular-nums transition-colors duration-150"
                          style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                        >
                          {headerLabel}
                        </button>
                        <button
                          type="button"
                          onClick={navNext}
                          aria-label={`Next ${view === 'days' ? 'month' : view === 'months' ? 'year' : 'decade'}`}
                          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
                        >
                          <CaretRight className="h-3.5 w-3.5" weight="bold" />
                        </button>
                      </div>

                      {view === 'days' && (
                        <>
                          {/* Weekday header */}
                          <div className="grid grid-cols-7 gap-0.5 px-2.5 pb-0.5">
                            {WEEKDAYS.map((w, i) => (
                              <div
                                key={i}
                                className="text-center text-muted-foreground/70 dark:text-[#62666d] uppercase"
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 590,
                                  letterSpacing: '0.06em',
                                  lineHeight: '18px',
                                }}
                              >
                                {w}
                              </div>
                            ))}
                          </div>

                          {/* Day grid */}
                          <div className="grid grid-cols-7 gap-0.5 px-2.5 pb-2.5">
                            {grid.map(({ date, inMonth }, i) => {
                              const isToday = date.toDateString() === todayKey
                              const isSelected = selKey && date.toDateString() === selKey
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => handleDayClick(date)}
                                  className={cn(
                                    'h-7 rounded-md tabular-nums transition-colors duration-100',
                                    isSelected
                                      ? 'bg-[#5e6ad2] text-white dark:bg-[#5e6ad2] hover:bg-[#7170ff] dark:hover:bg-[#7170ff]'
                                      : isToday
                                        ? cn(
                                            'border border-[#5e6ad2]/40 hover:bg-muted/40',
                                            'dark:border-[#7170ff]/40 dark:hover:bg-white/[0.05]',
                                            inMonth
                                              ? 'text-foreground dark:text-[#f7f8f8]'
                                              : 'text-muted-foreground/40 dark:text-[#3e3e44]',
                                          )
                                        : inMonth
                                          ? 'text-foreground hover:bg-muted/40 dark:text-[#f7f8f8] dark:hover:bg-white/[0.05]'
                                          : 'text-muted-foreground/40 hover:bg-muted/30 dark:text-[#3e3e44] dark:hover:bg-white/[0.04]',
                                  )}
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: isSelected ? 590 : 510,
                                    letterSpacing: '-0.05px',
                                  }}
                                >
                                  {date.getDate()}
                                </button>
                              )
                            })}
                          </div>
                        </>
                      )}

                      {view === 'months' && (
                        <div className="grid grid-cols-3 gap-1.5 px-3 pb-3 pt-1">
                          {MONTHS.map((mname, idx) => {
                            const isSelected =
                              selectedDate &&
                              selectedDate.getFullYear() === monthCursor.getFullYear() &&
                              selectedDate.getMonth() === idx
                            const isCurrentMonth = monthCursor.getMonth() === idx
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  setMonthCursor(new Date(monthCursor.getFullYear(), idx, 1))
                                  setView('days')
                                }}
                                className={cn(
                                  'h-9 rounded-md transition-colors duration-100',
                                  isSelected
                                    ? 'bg-[#5e6ad2] text-white dark:bg-[#5e6ad2] hover:bg-[#7170ff] dark:hover:bg-[#7170ff]'
                                    : isCurrentMonth
                                      ? 'border border-[#5e6ad2]/40 text-foreground dark:text-[#f7f8f8] dark:border-[#7170ff]/40 hover:bg-muted/40 dark:hover:bg-white/[0.05]'
                                      : 'text-foreground hover:bg-muted/40 dark:text-[#f7f8f8] dark:hover:bg-white/[0.05]',
                                )}
                                style={{
                                  fontSize: '12px',
                                  fontWeight: isSelected ? 590 : 510,
                                  letterSpacing: '-0.1px',
                                }}
                              >
                                {mname.slice(0, 3)}
                              </button>
                            )
                          })}
                        </div>
                      )}

                      {view === 'years' && (() => {
                        const startYear = Math.floor(monthCursor.getFullYear() / 12) * 12
                        const years = Array.from({ length: 12 }, (_, k) => startYear + k)
                        const todayYear = new Date().getFullYear()
                        return (
                          <div className="grid grid-cols-3 gap-1.5 px-3 pb-3 pt-1">
                            {years.map((y) => {
                              const isSelected = selectedDate && selectedDate.getFullYear() === y
                              const isCurrentYear = y === todayYear
                              return (
                                <button
                                  key={y}
                                  type="button"
                                  onClick={() => {
                                    setMonthCursor(new Date(y, monthCursor.getMonth(), 1))
                                    setView('months')
                                  }}
                                  className={cn(
                                    'h-9 rounded-md tabular-nums transition-colors duration-100',
                                    isSelected
                                      ? 'bg-[#5e6ad2] text-white dark:bg-[#5e6ad2] hover:bg-[#7170ff] dark:hover:bg-[#7170ff]'
                                      : isCurrentYear
                                        ? 'border border-[#5e6ad2]/40 text-foreground dark:text-[#f7f8f8] dark:border-[#7170ff]/40 hover:bg-muted/40 dark:hover:bg-white/[0.05]'
                                        : 'text-foreground hover:bg-muted/40 dark:text-[#f7f8f8] dark:hover:bg-white/[0.05]',
                                  )}
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: isSelected ? 590 : 510,
                                    letterSpacing: '-0.05px',
                                  }}
                                >
                                  {y}
                                </button>
                              )
                            })}
                          </div>
                        )
                      })()}
                    </>
                  )
                })()}

                {/* Time row — hour : minute + AM/PM.
                    Native browser number-spinner arrows are hidden via
                    `appearance-textfield + ::-webkit-(inner|outer)-spin-button`.
                    Replaced by:
                    • mouse-wheel over the field (deltaY ↑ = +1, ↓ = -1)
                    • keyboard ↑ / ↓ (native for type=number)
                    • subtle phosphor `CaretUpDown` glyph on the right edge as
                      a discoverability cue (dim, brightens on hover/focus). */}
                {showTime && (
                <div className={cn(
                  'flex items-center gap-2 px-3 py-3',
                  showDate && 'border-t border-border/50 dark:border-white/[0.06]',
                )}>
                  <span
                    className="text-muted-foreground dark:text-[#8a8f98] uppercase"
                    style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                  >
                    Time
                  </span>
                  <div className="flex items-center gap-1 ml-auto">
                    <TimeNumberField
                      value={h12}
                      displayValue={String(h12)}
                      min={1}
                      max={12}
                      onChange={handleHourChange}
                      ariaLabel="Hour"
                    />
                    <span
                      className="text-muted-foreground dark:text-[#8a8f98]"
                      style={{ fontSize: '12px', fontWeight: 590 }}
                    >
                      :
                    </span>
                    <TimeNumberField
                      value={m}
                      displayValue={String(m).padStart(2, '0')}
                      min={0}
                      max={59}
                      onChange={handleMinuteChange}
                      ariaLabel="Minute"
                    />
                    <button
                      type="button"
                      onClick={handleAmPmToggle}
                      aria-label={`Switch to ${isPM ? 'AM' : 'PM'}`}
                      className={cn(
                        'h-7 px-2 rounded-md border transition-colors duration-150 tabular-nums',
                        'bg-muted/40 border-border/60 text-foreground hover:bg-muted/60 hover:border-border/80',
                        'dark:bg-white/[0.04] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:hover:bg-white/[0.06] dark:hover:border-white/[0.14]',
                      )}
                      style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '0.04em' }}
                    >
                      {isPM ? 'PM' : 'AM'}
                    </button>
                  </div>
                </div>
                )}

                {/* Footer — Now + Done */}
                <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/20 dark:bg-white/[0.015]">
                  <button
                    type="button"
                    onClick={handleNow}
                    className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline underline-offset-4 transition-colors"
                    style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    Now
                  </button>
                  <button
                    type="button"
                    onClick={closePicker}
                    className={cn(
                      'h-7 px-3 rounded-md text-white shadow-sm transition-colors duration-150',
                      'bg-[#5e6ad2] hover:bg-[#7170ff] active:bg-[#5058c0]',
                    )}
                    style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    Done
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// TimeNumberField — number input with browser spinner suppressed.
// Scroll wheel + arrow keys + direct typing change the value. A subtle
// `CaretUpDown` glyph on the right hints that the field is steppable.

interface TimeNumberFieldProps {
  value: number
  displayValue: string
  min: number
  max: number
  onChange: (next: number) => void
  ariaLabel: string
}

function TimeNumberField({
  value,
  displayValue,
  min,
  max,
  onChange,
  ariaLabel,
}: TimeNumberFieldProps) {
  const [wheelOpen, setWheelOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)

  // Local string buffer so the user can clear the field and type freely.
  // The controlled `value`/`displayValue` props get committed only on a
  // valid in-range number. Buffer resyncs when the field is not focused.
  const [buffer, setBuffer] = useState<string>(displayValue)
  const [focused, setFocused] = useState(false)
  useEffect(() => {
    if (!focused) setBuffer(displayValue)
  }, [displayValue, focused])

  const ITEM_HEIGHT = 28
  const VISIBLE = 3
  const PADDING = ITEM_HEIGHT * Math.floor(VISIBLE / 2)
  const WHEEL_HEIGHT = ITEM_HEIGHT * VISIBLE
  const items = Array.from({ length: max - min + 1 }, (_, i) => min + i)

  const positionWheel = () => {
    if (!wrapperRef.current) return
    const rect = wrapperRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const top = spaceBelow < WHEEL_HEIGHT + 12 && rect.top > WHEEL_HEIGHT + 12
      ? rect.top - WHEEL_HEIGHT - 6
      : rect.bottom + 6
    setCoords({ top, left: rect.left, width: rect.width })
  }

  const openWheel = () => {
    positionWheel()
    setWheelOpen(true)
  }
  const closeWheel = () => setWheelOpen(false)

  // Sync scroll position to current value when wheel opens / value changes
  useLayoutEffect(() => {
    if (wheelOpen && scrollerRef.current) {
      scrollerRef.current.scrollTop = (value - min) * ITEM_HEIGHT
    }
  }, [wheelOpen, value, min])

  // Outside click + Escape closes the wheel
  useEffect(() => {
    if (!wheelOpen) return
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapperRef.current?.contains(t)) return
      const wheel = document.getElementById('time-wheel-menu')
      if (wheel?.contains(t)) return
      closeWheel()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeWheel()
    }
    document.addEventListener('mousedown', onMouseDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [wheelOpen])

  // Snap-scroll → live value update (debounced via rAF)
  const handleScroll = () => {
    if (!scrollerRef.current) return
    const idx = Math.round(scrollerRef.current.scrollTop / ITEM_HEIGHT)
    const next = Math.max(min, Math.min(max, min + idx))
    if (next !== value) onChange(next)
  }

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (document.activeElement !== e.currentTarget) return
    e.preventDefault()
    const delta = e.deltaY < 0 ? 1 : -1
    onChange(Math.max(min, Math.min(max, value + delta)))
  }

  return (
    <div ref={wrapperRef} className="relative group">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={buffer}
        onChange={(e) => {
          // Allow empty + partial input so user can clear and retype.
          const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 2)
          setBuffer(raw)
          if (raw === '') return
          const n = parseInt(raw)
          if (!Number.isNaN(n) && n >= min && n <= max) onChange(n)
        }}
        onFocus={(e) => {
          setFocused(true)
          // Select-all so typing immediately replaces — common datetime UX.
          e.currentTarget.select()
          openWheel()
        }}
        onBlur={() => {
          setFocused(false)
          const n = parseInt(buffer)
          if (Number.isNaN(n)) {
            setBuffer(displayValue) // restore last valid
          } else {
            const clamped = Math.max(min, Math.min(max, n))
            onChange(clamped)
          }
        }}
        onWheel={handleWheel}
        onClick={openWheel}
        onKeyDown={(e) => {
          // Keyboard arrow stepping (type=text doesn't get it natively)
          if (e.key === 'ArrowUp') {
            e.preventDefault()
            onChange(Math.min(max, value + 1))
          } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            onChange(Math.max(min, value - 1))
          } else if (e.key === 'Escape') {
            ;(e.currentTarget as HTMLInputElement).blur()
          }
        }}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={wheelOpen}
        data-no-spinner
        className={cn(
          'w-11 h-7 px-2 text-center rounded-md border tabular-nums transition-colors duration-150',
          'bg-background border-border/60 text-foreground',
          'hover:border-border/80',
          'focus:outline-none focus-visible:border-[#7170ff]/55',
          'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8]',
          'dark:hover:border-white/[0.14]',
          'dark:focus-visible:border-[#7170ff]/75',
        )}
        style={{ fontSize: '12px', fontWeight: 510 }}
      />

      {/* Wheel popover — opens on click/focus. Snap-scroll list with the
          centered value highlighted by a brand-soft band. */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {wheelOpen && coords && (
            <motion.div
              id="time-wheel-menu"
              role="listbox"
              aria-label={`${ariaLabel} wheel`}
              initial={{ opacity: 0, scale: 0.97, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -4 }}
              transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                width: Math.max(coords.width, 56),
                height: WHEEL_HEIGHT,
                zIndex: 300,
              }}
              className={cn(
                'rounded-md border overflow-hidden',
                'bg-card border-border/60 shadow-lg',
                'dark:bg-[#191a1b] dark:border-white/[0.08]',
                'dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
              )}
            >
              {/* Center highlight band — brand soft fill behind the selected row */}
              <div
                aria-hidden="true"
                className={cn(
                  'absolute left-0 right-0 pointer-events-none border-y',
                  'bg-[#5e6ad2]/[0.06] border-[#5e6ad2]/20',
                  'dark:bg-[#7170ff]/[0.10] dark:border-[#7170ff]/25',
                )}
                style={{ top: PADDING, height: ITEM_HEIGHT }}
              />

              {/* Scroller — snap-mandatory keeps items locked to row positions */}
              <div
                ref={scrollerRef}
                onScroll={handleScroll}
                className={cn(
                  'h-full overflow-y-auto snap-y snap-mandatory',
                  // Hide scrollbar on Webkit + Firefox
                  '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                )}
                style={{ paddingTop: PADDING, paddingBottom: PADDING }}
              >
                {items.map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="option"
                    aria-selected={n === value}
                    onClick={() => {
                      onChange(n)
                      closeWheel()
                      inputRef.current?.blur()
                    }}
                    className={cn(
                      'snap-center w-full flex items-center justify-center tabular-nums transition-colors duration-100',
                      n === value
                        ? 'text-foreground dark:text-[#f7f8f8]'
                        : 'text-muted-foreground/60 dark:text-[#62666d] hover:text-foreground dark:hover:text-[#d0d6e0]',
                    )}
                    style={{
                      height: ITEM_HEIGHT,
                      fontSize: '13px',
                      fontWeight: n === value ? 590 : 510,
                      letterSpacing: '-0.05px',
                    }}
                  >
                    {String(n).padStart(String(max).length, '0')}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}

export default DateTimePicker
